import React, { FC, useEffect, useMemo, useRef, useState, useContext } from "react";

import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { ProcedureButton } from "@/features/procedures/ui/components/common/ProcedureButton";
import { validationNewCourse } from "@/features/training/lib/FormValidation";
import {
	fromDateTimeLocalValue,
	toDateTimeLocalValue,
} from "@/features/training/lib/trainingDateUtils";
import {
	TrainingCourse,
	TrainingCourseDuration,
} from "@/features/training/model/Training/TrainingCourse";
import SATOSEditorField from "@/shared/forms/SATOSEditorField";
import CustomCheckbox from "@/shared/forms/CustomCheckbox";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import { AlertToast } from "@/shared/notifications/AlertToast";
import {
	sharedTaskDialogActionsSx,
	sharedTaskDialogContentSx,
	sharedTaskDialogHeaderSx,
	sharedTaskDialogPanelSx,
	sharedTaskDialogPaperSx,
} from "@/styles/sharedTaskDialogStyles";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { CancelActionButton, SaveActionButton } from "@/shared/ui";
import {
	Autocomplete,
	Box,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	IconButton,
	InputLabel,
	LinearProgress,
	MenuItem,
	Paper,
	Select,
	Stack,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {
	Field,
	type FieldProps,
	Form,
	Formik,
	useFormikContext,
} from "formik";

import {
	useFetchGroups,
	useFetchUserGroupMembershipStatus,
} from "@/service/GroupQueries";
import { useMutateCreateTrainingSession } from "@/service/TrainingMutations";
import {
	useFetchCourse,
	useFetchCourses,
	useFetchUserSummariesByCurrentCompany,
} from "@/service/TrainingQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


dayjs.extend(utc);
dayjs.extend(timezonePlugin);

/* ──────────────────────────────────────────────────────────────────────────── */
/* Types                                                                        */
/* ──────────────────────────────────────────────────────────────────────────── */

interface NewCourseSessionProps {
	courseId?: number;
	open: boolean;
	handleClose: () => void;
	selectedDate?: unknown;
}

type FormValues = {
	CourseId: number;
	Information: string;
	SessionDate: string; // datetime-local string
	ExpiresDate: string | null; // datetime-local string | null
	TrainerId: number | "";
	StudentIds: number[];
	GroupId: number | null;
	AddedGroupIds: number[];
	Duration: string;
	DisableExpiresDate: boolean;
};

/* ──────────────────────────────────────────────────────────────────────────── */
/* Helpers                                                                      */
/* ──────────────────────────────────────────────────────────────────────────── */

const getExpiresDateString = (
	duration: TrainingCourseDuration,
	tz: string,
): string | null => {
	const now = dayjs().tz(tz);

	switch (duration) {
		case TrainingCourseDuration.EndOfYear:
			return now.endOf("year").format("YYYY-MM-DDTHH:mm");
		case TrainingCourseDuration.OneYear:
			return now.add(1, "year").format("YYYY-MM-DDTHH:mm");
		case TrainingCourseDuration.TwoYears:
			return now.add(2, "year").format("YYYY-MM-DDTHH:mm");
		case TrainingCourseDuration.ThreeYears:
			return now.add(3, "year").format("YYYY-MM-DDTHH:mm");
		default:
			return null;
	}
};

function getRequestExpiresDate(
	courseDuration: TrainingCourseDuration,
	expiresDate: Date | string | null,
	disableExpiresDate: boolean,
	tz: string,
): Date | null {
	switch (true) {
		case courseDuration !== TrainingCourseDuration.Custom:
			return null;
		case disableExpiresDate:
			return null;
		default:
			return expiresDate && typeof expiresDate === "string"
				? fromDateTimeLocalValue(expiresDate, tz)
				: expiresDate instanceof Date
					? expiresDate
					: null;
	}
}

const humanizeDuration = (d: TrainingCourseDuration | string) =>
	String(d ?? "—")
		.replace(/([A-Z])/g, " $1")
		.trim();

const computeSessionDateInitial = (selectedDate: unknown, tz: string) => {
	if (!selectedDate) return dayjs().tz(tz).format("YYYY-MM-DDTHH:mm");

	return toDateTimeLocalValue(
		typeof selectedDate === "string"
			? selectedDate
			: (selectedDate as Date)?.toISOString?.(),
		tz,
	);
};

/* ──────────────────────────────────────────────────────────────────────────── */
/* Section header                                                               */
/* ──────────────────────────────────────────────────────────────────────────── */

const SectionHeader: FC<{
	title: React.ReactNode;
	subtitle?: React.ReactNode;
	icon?: React.ReactNode;
}> = ({ title, subtitle, icon }) => (
	<Box sx={{ mb: 1.5 }}>
		<Stack direction="row" alignItems="center" gap={1}>
			{icon}
			<Typography variant="subtitle1" fontWeight={700}>
				{title}
			</Typography>
		</Stack>
		{subtitle && (
			<Typography
				variant="caption"
				color="text.secondary"
				sx={{ display: "block", mt: 0.25 }}
			>
				{subtitle}
			</Typography>
		)}
	</Box>
);

const CourseFormStateSync: FC<{
	course?: TrainingCourse;
	timezone: string;
}> = ({ course, timezone }) => {
	const { values, setFieldValue } = useFormikContext<FormValues>();
	const lastSyncedCourseIdRef = useRef(values.CourseId);
	const resolvedCourse = course as
		| { Id?: number; Duration?: TrainingCourseDuration }
		| undefined;

	useEffect(() => {
		if (!resolvedCourse || resolvedCourse.Id !== values.CourseId) return;
		if (lastSyncedCourseIdRef.current === values.CourseId) return;

		lastSyncedCourseIdRef.current = values.CourseId;

		const duration =
			resolvedCourse.Duration ?? TrainingCourseDuration.OneYear;
		const disableExpiresDate = duration !== TrainingCourseDuration.Custom;

		void setFieldValue("Duration", humanizeDuration(duration), false);
		void setFieldValue(
			"ExpiresDate",
			getExpiresDateString(duration, timezone),
			false,
		);
		void setFieldValue("DisableExpiresDate", disableExpiresDate, false);
	}, [resolvedCourse, setFieldValue, timezone, values.CourseId]);

	return null;
};

/* ──────────────────────────────────────────────────────────────────────────── */
/* Dialog shell                                                                 */
/* ──────────────────────────────────────────────────────────────────────────── */

export const NewCourseSession: FC<NewCourseSessionProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const closeButtonSx = {
		color: theme.palette.text.secondary,
		"&:hover": {
			color: theme.palette.text.primary,
			backgroundColor: theme.palette.action.hover,
		},
	};

	return (
		<Dialog
			onClose={props.handleClose}
			open={props.open}
			fullWidth
			maxWidth="md"
			fullScreen={isMobile}
			aria-labelledby="new-training-session-title"
			PaperProps={{
				sx: sharedTaskDialogPaperSx(theme, isMobile),
			}}
		>
			<DialogTitle
				id="new-training-session-title"
				component="div"
				sx={{
					...sharedTaskDialogHeaderSx(theme),
					position: "relative",
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 1.5,
				}}
			>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						fontWeight: 800,
						letterSpacing: "0.08em",
						color: "primary.main",
						mb: 0.4,
					}}
				>
					<Translate String="Auto.Features.Training.Pages.TrainingTab.PanelRight.TrainingCourse.NewCourseSession.L226C6.TRAINING">TRAINING</Translate>
				</Typography>
				<Typography
					variant="h6"
					component="h2"
					sx={{ fontWeight: 700, color: "text.primary", pr: 5, mb: 0.75 }}
				>
					<Translate String="Training.NewTrainingSession.Title">
						New training session
					</Translate>
				</Typography>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ mt: 0.5, display: "block" }}
				>
					<Translate String="Training.NewTrainingSession.Subtitle">
						Schedule a new session, add participants, and set expiry.
					</Translate>
				</Typography>
				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.TrainingTab.PanelRight.TrainingCourse.NewCourseSession.L248C17.Close", "Close")}
					onClick={props.handleClose}
					size="small"
					sx={{ position: "absolute", right: 8, top: 8, ...closeButtonSx }}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent
				sx={{
					...sharedTaskDialogContentSx(theme),
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 0,
					flex: 1,
					overflow: "auto",
				}}
			>
				<NewTrainingSessionForm {...props} />
			</DialogContent>
		</Dialog>
	);
};

/* ──────────────────────────────────────────────────────────────────────────── */
/* Form                                                                         */
/* ──────────────────────────────────────────────────────────────────────────── */

export const NewTrainingSessionForm: FC<NewCourseSessionProps> = (props) => {
	const { timezone } = useTime();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	// keep local choice, but DON'T remount Formik via key
	const [courseId, setCourseId] = useState<number | undefined>(props.courseId);

	// data
	const { isLoading: isLoadingCourses, data: courses } = useFetchCourses();
	const effectiveCourseId =
		courseId ?? props.courseId ?? (courses ? courses[0]?.Id : undefined) ?? 0;

	const { isLoading: isLoadingCourse, data: course } =
		useFetchCourse(effectiveCourseId);
	const { isLoading: isLoadingTrainerOptions, data: trainerData } =
		useFetchUserSummariesByCurrentCompany();
	const { isLoading: isLoadingGroups, data: groupsRaw } = useFetchGroups();

	const trainerOptions = Array.isArray(trainerData) ? trainerData : [];
	const groups = Array.isArray(groupsRaw) ? groupsRaw : [];

	// group add flow
	const [groupToAdd, setGroupToAdd] = useState<number | null>(null);
	const { data: groupMembersData } = useFetchUserGroupMembershipStatus(
		groupToAdd ?? 0,
		{
			enabled: groupToAdd != null && groupToAdd > 0,
		},
	);

	const [pendingGroupAdd, setPendingGroupAdd] = useState<{
		groupId: number;
		setFieldValue: (f: string, v: unknown) => Promise<unknown> | void;
		currentIds: number[];
	} | null>(null);

	useEffect(() => {
		if (!pendingGroupAdd || !groupMembersData?.Items) return;

		const { groupId, setFieldValue, currentIds } = pendingGroupAdd;
		const members = groupMembersData.Items;

		const memberIds = members
			.filter(
				(m: { GroupMembershipType?: { IsMember?: boolean } }) =>
					m.GroupMembershipType?.IsMember !== false,
			)
			.map((m: { Id: number }) => m.Id);

		const merged = Array.from(new Set([...currentIds, ...memberIds]));
		void setFieldValue("StudentIds", merged);
		void setFieldValue("GroupId", groupId);

		const groupName = groups.find((g) => g.Id === groupId)?.Name ?? "group";
		void AlertToast.success(
			`${memberIds.length} members added from ${groupName}`,
		);

		setPendingGroupAdd(null);
		setGroupToAdd(null);
	}, [pendingGroupAdd, groupMembersData, groups]);

	const createTrainingSession = useMutateCreateTrainingSession();

	const sortedCourses = useMemo(
		() => courses?.slice().sort((a, b) => a.Name.localeCompare(b.Name)) ?? [],
		[courses],
	);

	// ✅ Freeze initial values while dialog is open so refetches won't wipe user input
	const frozenInitialValuesRef = useRef<FormValues | null>(null);

	useEffect(() => {
		// when dialog closes, clear ref so next open starts fresh
		if (!props.open) {
			frozenInitialValuesRef.current = null;
			return;
		}

		// only build once per open
		if (frozenInitialValuesRef.current) return;
		if (!courses || courses.length === 0) return;
		if (effectiveCourseId > 0 && isLoadingCourse) return;

		const initialCourseId = props.courseId ?? courseId ?? courses[0]?.Id ?? 0;
		const duration = course?.Duration ?? TrainingCourseDuration.OneYear;

		frozenInitialValuesRef.current = {
			CourseId: initialCourseId,
			Information: "",
			SessionDate: computeSessionDateInitial(props.selectedDate, timezone),
			ExpiresDate: getExpiresDateString(duration, timezone),
			TrainerId: "",
			StudentIds: [],
			GroupId: null,
			AddedGroupIds: [],
			Duration: humanizeDuration(duration),
			DisableExpiresDate: duration !== TrainingCourseDuration.Custom,
		};
	}, [
		props.open,
		courses,
		props.courseId,
		courseId,
		props.selectedDate,
		timezone,
		course,
		effectiveCourseId,
		isLoadingCourse,
	]);

	const handleSubmit = async (values: FormValues) => {
		try {
			await createTrainingSession({
				CourseId: Number(values.CourseId),
				Information: String(values.Information ?? ""),
				SessionDate: fromDateTimeLocalValue(values.SessionDate, timezone),
				ExpiresDate: getRequestExpiresDate(
					course?.Duration ?? TrainingCourseDuration.OneYear,
					values.ExpiresDate,
					Boolean(values.DisableExpiresDate),
					timezone,
				),
				TrainerId:
					values.TrainerId != null && values.TrainerId !== ""
						? Number(values.TrainerId)
						: 0,
				StudentIds: Array.isArray(values.StudentIds)
					? values.StudentIds.map((v) => Number(v))
					: [],
				GroupId: values.GroupId == null ? null : Number(values.GroupId),
			});

			props.handleClose();
			AlertRequest.success();
		} catch (error) {
			console.error(error);
			props.handleClose();
			AlertRequest.failed();
		}
	};

	const isLoading =
		isLoadingTrainerOptions ||
		isLoadingGroups ||
		isLoadingCourses ||
		!courses;

	if (isLoading || !frozenInitialValuesRef.current) {
		return (
			<Box
				sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
			>
				<LinearProgress />
			</Box>
		);
	}

	return (
		<Formik<FormValues>
			initialValues={frozenInitialValuesRef.current}
			validationSchema={validationNewCourse}
			onSubmit={handleSubmit}
		>
			{({ values, setFieldValue, isSubmitting }) => {
				const sectionCardSx = {
					...sharedTaskDialogPanelSx(theme),
					p: { xs: 2, md: 2.5 },
				};
				const selectedIds = Array.isArray(values.StudentIds)
					? values.StudentIds.map(Number).filter(Boolean)
					: [];

				const selectedUsers = trainerOptions.filter((u) =>
					selectedIds.includes(u.Id),
				);
				const selectedGroupIds = Array.isArray(values.AddedGroupIds)
					? values.AddedGroupIds.map(Number).filter(Boolean)
					: [];
				const selectedGroups = groups.filter((group) =>
					selectedGroupIds.includes(group.Id),
				);

				const formCourseDuration =
					(course as { Duration?: TrainingCourseDuration } | undefined)
						?.Duration ?? TrainingCourseDuration.OneYear;

				const isCustomDuration =
					formCourseDuration === TrainingCourseDuration.Custom;

				return (
					<Form>
						<CourseFormStateSync course={course} timezone={timezone} />
						<Stack spacing={3}>
							{/* 1. Session */}
							<Paper variant="outlined" sx={sectionCardSx}>
								<SectionHeader
									icon={
										<CalendarTodayIcon
											fontSize="small"
											sx={{ color: "text.secondary" }}
										/>
									}
									title={
										<Translate String="Training.NewTrainingSession.Section.Session">
											Session
										</Translate>
									}
									subtitle={
										<Translate String="Training.NewTrainingSession.Section.Session.Desc">
											Select course, date and duration.
										</Translate>
									}
								/>

								<Stack spacing={2}>
									{!props.courseId && (
										<Field name="CourseId">
											{({
												field,
												meta,
												form,
											}: FieldProps<number, FormValues>) => (
												<Autocomplete
													options={sortedCourses}
													loading={isLoadingCourse}
													getOptionLabel={(option) => option.Name ?? ""}
													isOptionEqualToValue={(option, value) =>
														option.Id === value.Id
													}
													value={
														sortedCourses.find(
															(courseOption) =>
																courseOption.Id === Number(field.value),
														) ?? null
													}
													onChange={(_, newValue) => {
														const next = newValue?.Id ?? 0;
														void form.setFieldValue("CourseId", next);
														void form.setFieldTouched("CourseId", true, false);
														setCourseId(next || undefined);
													}}
													renderInput={(params) => (
														<TextField
															{...params}
															label={
																<Translate String="Training.NewTrainingSession.Course">
																	Course
																</Translate>
															}
															placeholder="Search courses..."
															size="small"
															error={meta.touched && Boolean(meta.error)}
															helperText={meta.touched && meta.error}
															sx={{
																"& .MuiOutlinedInput-root": {
																	borderRadius: 2,
																	bgcolor: "background.paper",
																},
															}}
														/>
													)}
												/>
											)}
										</Field>
									)}

									<Field name="SessionDate">
										{({ field, meta }: FieldProps<string, FormValues>) => (
											<TextField
												{...field}
												label={
													<Translate String="Training.NewTrainingSession.Date">
														Date
													</Translate>
												}
												type="datetime-local"
												fullWidth
												size="small"
												error={meta.touched && Boolean(meta.error)}
												helperText={meta.touched && meta.error}
												InputLabelProps={{ shrink: true }}
												sx={{
													"& .MuiOutlinedInput-root": {
														borderRadius: 2,
														bgcolor: "background.paper",
													},
												}}
											/>
										)}
									</Field>

									<TextField
										label={
											<Translate String="Training.NewTrainingSession.CourseDuration">
												Course Duration
											</Translate>
										}
										value={humanizeDuration(formCourseDuration)}
										InputProps={{ readOnly: true }}
										fullWidth
										size="small"
										sx={{
											"& .MuiOutlinedInput-root": {
												borderRadius: 2,
												bgcolor: "action.hover",
											},
										}}
									/>
								</Stack>
							</Paper>

							{/* 2. Scheduling */}
							<Paper variant="outlined" sx={sectionCardSx}>
								<SectionHeader
									icon={
										<ScheduleIcon
											fontSize="small"
											sx={{ color: "text.secondary" }}
										/>
									}
									title={
										<Translate String="Training.NewTrainingSession.Section.Scheduling">
											Scheduling
										</Translate>
									}
									subtitle={
										<Translate String="Training.NewTrainingSession.Section.Scheduling.Desc">
											Set expiry date or disable for custom duration.
										</Translate>
									}
								/>

								<Stack spacing={2}>
									<Field name="ExpiresDate">
										{({
											field,
											meta,
										}: FieldProps<string | null, FormValues>) => (
											<TextField
												{...field}
												label={
													<Translate String="Training.NewTrainingSession.ExpiryDate">
														Expiry Date
													</Translate>
												}
												type="datetime-local"
												fullWidth
												size="small"
												disabled={values.DisableExpiresDate}
												InputProps={{ readOnly: values.DisableExpiresDate }}
												error={meta.touched && Boolean(meta.error)}
												helperText={
													meta.touched && meta.error
														? meta.error
														: values.DisableExpiresDate
															? "Expiry date is disabled for this course duration."
															: "Optional, depends on course duration."
												}
												InputLabelProps={{ shrink: true }}
												sx={{
													"& .MuiOutlinedInput-root": {
														borderRadius: 2,
														bgcolor: "background.paper",
													},
												}}
											/>
										)}
									</Field>

									{isCustomDuration && (
										<Box sx={{ display: "inline-flex" }}>
											<CustomCheckbox
												id="DisableExpiresDate"
												name="DisableExpiresDate"
												type="checkbox"
												label="Disable expiry"
												translationString="Training.NewTrainingSession.Disable"
												asSwitch
											/>
										</Box>
									)}
								</Stack>
							</Paper>

							{/* 3. People */}
							<Paper variant="outlined" sx={sectionCardSx}>
								<SectionHeader
									icon={
										<PersonIcon
											fontSize="small"
											sx={{ color: "text.secondary" }}
										/>
									}
									title={
										<Translate String="Training.NewTrainingSession.Section.People">
											People
										</Translate>
									}
									subtitle={
										<Translate String="Training.NewTrainingSession.Section.People.Desc">
											Assign trainer and participants.
										</Translate>
									}
								/>

								<Stack spacing={2}>
									<Field name="TrainerId">
										{({
											field,
											meta,
											form,
										}: FieldProps<number | "", FormValues>) => (
											<FormControl
												fullWidth
												size="small"
												error={meta.touched && Boolean(meta.error)}
												sx={{
													"& .MuiOutlinedInput-root": {
														borderRadius: 2,
														bgcolor: "background.paper",
													},
												}}
											>
												<InputLabel shrink>
													<Translate String="Training.NewTrainingSession.Trainer">
														Trainer
													</Translate>
												</InputLabel>

												<Select
													{...field}
													label={
														<Translate String="Training.NewTrainingSession.Trainer">
															Trainer
														</Translate>
													}
													value={field.value ?? ""}
													onChange={(e) => {
														const v = e.target.value;
														void form.setFieldValue(
															"TrainerId",
															v === "" ? "" : Number(v),
														);
													}}
													displayEmpty
												>
													<MenuItem value="">
														<em>Select trainer…</em>
													</MenuItem>
													{trainerOptions.map((x) => (
														<MenuItem key={x.Id} value={x.Id}>
															{x.Name}
														</MenuItem>
													))}
												</Select>

												{meta.touched && meta.error && (
													<FormHelperText>{meta.error}</FormHelperText>
												)}
											</FormControl>
										)}
									</Field>

									<Box>
										<Stack
											direction="row"
											alignItems="center"
											justifyContent="space-between"
											flexWrap="wrap"
											gap={1}
											sx={{ mb: 1 }}
										>
											<Typography variant="subtitle2" fontWeight={600}>
												<Translate String="Training.EditTrainingSession.Participants">
													Participants
												</Translate>
												{selectedIds.length > 0 && (
													<Typography
														component="span"
														variant="body2"
														color="primary"
														sx={{ ml: 1, fontWeight: 600 }}
													>
														({selectedIds.length} selected)
													</Typography>
												)}
											</Typography>

											<Stack direction="row" spacing={1}>
												<ProcedureButton
													size="small"
													variant="outlined"
													onClick={() => {
														void setFieldValue(
															"StudentIds",
															trainerOptions.map((u) => u.Id),
														);
													}}
													sx={{ whiteSpace: "nowrap" }}
												>
													Select all
												</ProcedureButton>
												<ProcedureButton
													size="small"
													variant="outlined"
													intent="warning"
													onClick={() => {
														void setFieldValue("StudentIds", []);
														void setFieldValue("AddedGroupIds", []);
														void setFieldValue("GroupId", null);
														setGroupToAdd(null);
														setPendingGroupAdd(null);
													}}
													sx={{ whiteSpace: "nowrap" }}
												>
													Clear all
												</ProcedureButton>
											</Stack>
										</Stack>

										<Autocomplete
											multiple
											options={trainerOptions}
											getOptionLabel={(opt) => opt.Name ?? ""}
											value={selectedUsers}
											disableCloseOnSelect
											onChange={(_, newVal) => {
												void setFieldValue(
													"StudentIds",
													newVal.map((u) => u.Id),
												);
											}}
											filterSelectedOptions
											renderInput={(params) => (
												<TextField
													{...params}
													placeholder="Search participants…"
													size="small"
													sx={{
														"& .MuiOutlinedInput-root": {
															bgcolor: "background.paper",
															borderRadius: 2,
														},
													}}
												/>
											)}
											renderOption={(renderProps, option, { selected }) => (
												<li
													{...renderProps}
													key={option.Id}
													style={{
														borderBottom: "1px solid",
														borderColor: "divider",
													}}
												>
													<Checkbox
														checked={selected}
														size="small"
														sx={{ mr: 1 }}
													/>
													{option.Name}
												</li>
											)}
										/>
									</Box>

									{/* Add by group */}
									<Box>
										<Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
											<Translate String="Training.EditTrainingSession.AddByGroup">
												Add by group
											</Translate>
											{selectedGroupIds.length > 0 && (
												<Typography
													component="span"
													variant="body2"
													color="primary"
													sx={{ ml: 1, fontWeight: 600 }}
												>
													({selectedGroupIds.length}{" "}
													<Translate String="Common.Selected">selected</Translate>)
												</Typography>
											)}
										</Typography>

										<Autocomplete
											multiple
											options={groups}
											getOptionLabel={(option) => option.Name ?? ""}
											value={selectedGroups}
											filterSelectedOptions
											disableClearable
											sx={{
												"& .MuiOutlinedInput-root": {
													height: "auto",
													minHeight: 40,
													alignItems: "flex-start",
													flexWrap: "wrap",
													bgcolor: "background.paper",
													borderRadius: 2,
													py: 0.5,
												},
											}}
											onChange={(_, newValue, reason) => {
												const nextSelectedGroupIds = newValue
													.map((group) => group.Id)
													.filter(
														(groupId): groupId is number =>
															typeof groupId === "number",
													);
												const newlyAddedGroup = newValue.find(
													(group) => !selectedGroupIds.includes(group.Id),
												);

												void setFieldValue(
													"AddedGroupIds",
													nextSelectedGroupIds,
												);

												if (
													reason === "removeOption" ||
													reason === "clear"
												) {
													void setFieldValue(
														"GroupId",
														nextSelectedGroupIds.at(-1) ?? null,
													);
													if (nextSelectedGroupIds.length === 0) {
														setGroupToAdd(null);
														setPendingGroupAdd(null);
													}
													return;
												}

												if (!newlyAddedGroup) {
													return;
												}

												void setFieldValue("GroupId", newlyAddedGroup.Id);
												setGroupToAdd(newlyAddedGroup.Id);
												setPendingGroupAdd({
													groupId: newlyAddedGroup.Id,
													setFieldValue,
													currentIds: selectedIds,
												});
											}}
											renderInput={(params) => (
												<TextField
													{...params}
													label={
														<Translate String="Training.NewTrainingSession.AddGroup">
															Group
														</Translate>
													}
													size="small"
												/>
											)}
											renderOption={(renderProps, option, { selected }) => (
												<li
													{...renderProps}
													key={option.Id}
													style={{
														borderBottom: "1px solid",
														borderColor: "divider",
													}}
												>
													<Checkbox
														checked={selected}
														size="small"
														sx={{ mr: 1 }}
													/>
													{option.Name ?? ""}
												</li>
											)}
										/>
										{selectedGroups.length > 0 && (
											<Stack
												direction="row"
												flexWrap="wrap"
												gap={1}
												sx={{ mt: 1 }}
											>
												{selectedGroups.map((group) => (
													<Chip
														key={group.Id}
														label={group.Name ?? ""}
														size="small"
													/>
												))}
											</Stack>
										)}
									</Box>
								</Stack>
							</Paper>

							{/* 4. Notes */}
							<Paper variant="outlined" sx={sectionCardSx}>
								<SectionHeader
									title={
										<Translate String="Training.NewTrainingSession.Section.Notes">
											Notes
										</Translate>
									}
									subtitle={
										<Translate String="Training.NewTrainingSession.Section.Notes.Desc">
											Additional information about this session.
										</Translate>
									}
								/>

								<SATOSEditorField
									id="Information"
									name="Information"
									label="Information"
									style={{ minWidth: "100%" }}
									imageUploadDisabled={true}
								/>
							</Paper>
						</Stack>

						{/* Footer */}
						<DialogActions
							sx={{
								...sharedTaskDialogActionsSx(theme, isMobile),
								px: 0,
								pt: 2,
								mt: 3,
								display: "flex",
								justifyContent: "flex-end",
								gap: 1.5,
								flexWrap: "wrap",
							}}
						>
							<CancelActionButton
								onClick={props.handleClose}
								disabled={isSubmitting}
							>
								<Translate String="Procedure.Button.Cancel">Cancel</Translate>
							</CancelActionButton>

							<SaveActionButton
								type="submit"
								loading={isSubmitting}
								loadingText={
									<Translate String="LabDaily.Saving">Saving...</Translate>
								}
								disabled={isSubmitting}
							>
								<Translate String="Training.NewTrainingSession.CreateButton">
									Create session
								</Translate>
							</SaveActionButton>
						</DialogActions>
					</Form>
				);
			}}
		</Formik>
	);
};
